"use client";

import { useState } from "react";
import Link from "next/link";
import { Loader2 } from "lucide-react";
import { useLanguageStore } from "@/stores/language";
import {
  CREDITS_PER_VIDEO,
  TOPUP_TTL_DAYS,
} from "@/lib/credits-config";
import type { TopupPrice } from "@/lib/stripe";
import { t } from "@/lib/i18n";

interface PricingClientProps {
  isLoggedIn: boolean;
  balance: number;
  prices: TopupPrice[];
}

function formatAmount(amount: number, currency: string): string {
  const label = currency.toLowerCase() === "hkd" ? "HK$" : currency.toUpperCase() + " ";
  const whole = amount / 100;
  return `${label}${Number.isInteger(whole) ? whole : whole.toFixed(2)}`;
}

export function PricingClient({ isLoggedIn, balance, prices }: PricingClientProps) {
  const lang = useLanguageStore((s) => s.language);
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleBuy = async (priceId: string) => {
    setError(null);
    if (!isLoggedIn) {
      window.location.href = `/login?callbackUrl=${encodeURIComponent("/pricing")}`;
      return;
    }
    setLoadingPriceId(priceId);
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priceId }),
      });
      const data = await res.json();
      if (data?.success && data.data?.url) {
        window.location.href = data.data.url;
        return;
      }
      setError(
        data?.error?.message ??
          t("pricing.errCheckout", lang)
      );
    } catch {
      setError(t("pricing.errNetwork", lang));
    } finally {
      setLoadingPriceId(null);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">
          {t("pricing.topUp", lang)}
        </h1>
        <p className="text-muted-foreground">
          {t("pricing.explainer", lang, { credits: CREDITS_PER_VIDEO })}
        </p>
      </div>

      {isLoggedIn && (
        <div className="rounded-lg border border-border bg-muted/40 p-4 text-center">
          <div className="text-sm text-muted-foreground">
            {t("pricing.yourBalance", lang)}
          </div>
          <div className="mt-1 text-2xl font-semibold tabular-nums">
            {balance.toLocaleString()}{" "}
            <span className="text-sm font-normal text-muted-foreground">
              {t("credits.unit", lang)}
            </span>
          </div>
          <Link
            href="/profile"
            className="mt-2 inline-block text-xs font-medium text-primary underline-offset-2 hover:underline"
          >
            {t("pricing.viewHistory", lang)}
          </Link>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Top-up packs — each pack is a large clickable card. */}
      {prices.length === 0 ? (
        <div className="rounded-xl border border-border p-8 text-center text-sm text-muted-foreground">
          {t("pricing.unavailable", lang)}
        </div>
      ) : (
        <div className="space-y-3">
          {prices.map((p) => {
            const money = formatAmount(p.unitAmount, p.currency);
            const videos = Math.floor(p.credits / CREDITS_PER_VIDEO);
            const isLoading = loadingPriceId === p.priceId;
            return (
              <button
                key={p.priceId}
                type="button"
                onClick={() => handleBuy(p.priceId)}
                disabled={isLoading}
                className="flex w-full items-center justify-between gap-4 rounded-xl border-2 border-border bg-background p-5 text-left transition-colors hover:border-primary hover:bg-primary/5 active:bg-primary/10 disabled:opacity-60"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xl font-bold tabular-nums">
                    {p.credits.toLocaleString()}{" "}
                    <span className="text-sm font-normal text-muted-foreground">
                      {t("credits.unit", lang)}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {t("pricing.approxVideos", lang, { videos })}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  ) : (
                    <div className="text-xl font-bold text-primary tabular-nums">
                      {money}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
          <p className="pt-2 text-center text-xs text-muted-foreground">
            {t("pricing.validity", lang, { days: TOPUP_TTL_DAYS })}
          </p>
          <p className="text-center text-xs text-muted-foreground">
            {lang === "en" ? (
              <>
                By purchasing you agree to our{" "}
                <Link href="/terms" className="underline hover:text-foreground">
                  Terms
                </Link>
                . All purchases are non-refundable except as required by law.
              </>
            ) : (
              <>
                购买即表示您同意我们的
                <Link href="/terms" className="underline hover:text-foreground">
                  服务条款
                </Link>
                。除适用法律另有要求外，所有购买不予退款。
              </>
            )}
          </p>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">
        {t("pricing.freeReuse", lang)}
      </p>
    </div>
  );
}

